import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  Car, ArrowRight, Check, Shield,
  Share2, Image, ChevronRight,
  Clock, Sparkles, Users,
  HeartHandshake, CalendarCheck, Store, Eye, X, Search,
  TrendingUp, Phone, BarChart3, Layers,
  Heart, MessageCircle, Send, Bookmark,
  Globe, ChevronDown, Star, Play,
  Sun, Moon
} from 'lucide-react';

/* ================================================= */
/* THEME DEFINITIONS                                 */
/* ================================================= */
const THEMES = {
  dark: {
    bg: '#111114',
    bgAlt: '#18181b',
    card: '#1c1c21',
    surface: '#18181b',
    border: 'rgba(255,255,255,0.06)',
    borderSolid: '#2a2a30',
    input: '#1c1c21',
    hover: '#252530',
    text: '#ffffff',
    textSecondary: '#9ca3af',
    textMuted: '#6b7280',
    textDim: '#4b5563',
    accent: '#C8381D',
    accentLight: '#D94A2E',
    accentText: '#ffffff',
    accentBg: 'rgba(200,56,29,0.10)',
    accentBorder: 'rgba(200,56,29,0.30)',
    navBg: 'rgba(17,17,20,0.80)',
    heroGlow: 'rgba(200,56,29,0.08)',
    greenDot: '#4ade80',
    greenText: '#4ade80',
    greenBg: 'rgba(34,197,94,0.10)',
    orangeText: '#fb923c',
    orangeBg: 'rgba(249,115,22,0.10)',
    blueText: '#60a5fa',
    blueBg: 'rgba(59,130,246,0.10)',
    redDot: 'rgba(239,68,68,0.60)',
    yellowDot: 'rgba(234,179,8,0.60)',
    greenDotBrowser: 'rgba(34,197,94,0.60)',
    shadow: 'rgba(0,0,0,0.3)',
  },
  light: {
    bg: '#faf8f3',
    bgAlt: '#f2ede5',
    card: '#ffffff',
    surface: '#f5f1ea',
    border: '#e5dfd4',
    borderSolid: '#ddd6c9',
    input: '#f5f1ea',
    hover: '#ece5d9',
    text: '#1c1810',
    textSecondary: '#6b6358',
    textMuted: '#9a9188',
    textDim: '#b8b0a5',
    accent: '#b83216',
    accentLight: '#C8381D',
    accentText: '#ffffff',
    accentBg: 'rgba(184,50,22,0.07)',
    accentBorder: 'rgba(184,50,22,0.25)',
    navBg: 'rgba(250,248,243,0.88)',
    heroGlow: 'rgba(200,56,29,0.05)',
    greenDot: '#16a34a',
    greenText: '#16a34a',
    greenBg: 'rgba(22,163,74,0.10)',
    orangeText: '#ea580c',
    orangeBg: 'rgba(234,88,12,0.10)',
    blueText: '#2563eb',
    blueBg: 'rgba(37,99,235,0.10)',
    redDot: 'rgba(220,38,38,0.70)',
    yellowDot: 'rgba(202,138,4,0.70)',
    greenDotBrowser: 'rgba(22,163,74,0.70)',
    shadow: 'rgba(0,0,0,0.08)',
  },
};


const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [website, setWebsite] = useState('');
  const [portals, setPortals] = useState('');
  const [stockSize, setStockSize] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [wantWebsite, setWantWebsite] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [navSolid, setNavSolid] = useState(false);
  const signupRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const heroRef = useRef(null);

  const t = isDark ? THEMES.dark : THEMES.light;

  useEffect(() => {
    const handleScroll = () => {
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      setNavSolid(heroBottom < 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSignup = () => {
    signupRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen overflow-x-hidden transition-colors duration-300" style={{ backgroundColor: t.bg, color: t.text }}>
      {/* PROMO BANNER — au-dessus de la nav */}
      <div className="fixed top-0 left-0 right-0 z-[60] text-center py-2 px-4 text-sm font-medium" style={{ background: `linear-gradient(to right, ${t.accent}, ${t.accentLight})`, color: '#ffffff' }}>
        <span className="hidden sm:inline">Offre de lancement — </span>
        <strong>-50% à vie</strong> sur toutes les formules pour les inscrits en février 2026.
        <button onClick={scrollToSignup} className="underline ml-2 hover:no-underline">En profiter →</button>
      </div>

      {/* NAVIGATION — transparent dans le hero, opaque au scroll */}
      <nav
        className="fixed top-[36px] left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: navSolid ? (isDark ? 'rgba(17,17,20,0.95)' : 'rgba(255,255,255,0.95)') : 'transparent',
          backdropFilter: navSolid ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: navSolid ? 'blur(20px)' : 'none',
          borderBottom: navSolid ? `1px solid ${t.border}` : '1px solid transparent',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Car className="h-7 w-7" style={{ color: t.accent }} />
            <span className="text-xl font-bold tracking-tight">Carmin</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm" style={{ color: navSolid ? t.textSecondary : (isDark ? 'rgba(255,255,255,0.8)' : 'rgba(28,24,16,0.6)') }}>
            <button onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' })} className="hover:opacity-80 transition-opacity">Fonctionnalités</button>
            <button onClick={() => pricingRef.current?.scrollIntoView({ behavior: 'smooth' })} className="hover:opacity-80 transition-opacity">Tarifs</button>
            <button onClick={scrollToSignup} className="hover:opacity-80 transition-opacity">S'inscrire</button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full transition-all"
              style={{ backgroundColor: navSolid ? t.accentBg : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(28,24,16,0.06)'), color: t.accent }}
              title={isDark ? 'Mode clair' : 'Mode sombre'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => navigate('/login')} className="text-sm hover:opacity-80 transition-opacity hidden sm:block" style={{ color: navSolid ? t.textSecondary : (isDark ? 'rgba(255,255,255,0.8)' : 'rgba(28,24,16,0.6)') }}>Connexion</button>
            <button onClick={scrollToSignup} className="text-sm font-medium px-5 py-2 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
          </div>
        </div>
      </nav>

      {/* ============================================ */}
      {/* HERO — full-bleed, 2 colonnes                */}
      {/* ============================================ */}
      <section
        ref={heroRef}
        className="relative min-h-[100vh] flex items-center overflow-hidden"
        style={{ background: isDark
          ? 'linear-gradient(135deg, #0a0a0f 0%, #111118 40%, #1a1020 70%, #0f0a18 100%)'
          : 'linear-gradient(135deg, #f5f0e8 0%, #ece5d9 40%, #f0e8e0 70%, #e8e0d8 100%)'
        }}
      >
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] rounded-full blur-[150px]" style={{ backgroundColor: isDark ? 'rgba(200,56,29,0.12)' : 'rgba(184,50,22,0.08)' }} />
          <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full blur-[120px]" style={{ backgroundColor: isDark ? 'rgba(120,40,200,0.06)' : 'rgba(184,50,22,0.04)' }} />
          <div className="absolute top-[30%] left-[40%] w-[300px] h-[300px] rounded-full blur-[100px]" style={{ backgroundColor: isDark ? 'rgba(200,56,29,0.05)' : 'rgba(184,50,22,0.03)' }} />
        </div>

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: isDark ? 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)' : 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="max-w-7xl mx-auto px-6 w-full pt-32 pb-20 md:pt-36 md:pb-28 relative z-10">
          <div className="md:flex items-center gap-12 lg:gap-20">
            {/* Colonne gauche — texte */}
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-5 landing-fade-in" style={{ color: t.accent }}>
                Le moteur digital des marchands automobiles
              </p>

              <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 landing-fade-in" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>
                Les véhicules viennent à vous.
                <br />
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${t.accent}, ${t.accentLight}, #f97316)` }}>
                  Les acheteurs aussi.
                </span>
              </h1>

              <p className="text-lg md:text-xl mb-8 leading-relaxed landing-fade-in landing-delay-1" style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(28,24,16,0.6)' }}>
                Carmin diffuse vos annonces partout et vous connecte à une marketplace
                entre pros pour acheter et vendre sans contraintes.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 landing-fade-in landing-delay-2">
                <button onClick={scrollToSignup} className="group font-semibold px-8 py-4 rounded-full transition-all hover:shadow-xl flex items-center gap-2 text-lg" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
                  Essayer Carmin
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => featuresRef.current?.scrollIntoView({ behavior: 'smooth' })} className="font-medium px-6 py-4 rounded-full transition-all flex items-center gap-2" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(28,24,16,0.6)', border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(28,24,16,0.15)' }}>
                  Voir la démo
                  <Play className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm mt-5 landing-fade-in landing-delay-2" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(28,24,16,0.4)' }}>
                On vous aide à démarrer, gratuitement · Annulation libre
              </p>

              {/* Channel badges */}
              <div className="mt-8 flex items-center gap-3 flex-wrap landing-fade-in landing-delay-2">
                {['Facebook', 'Instagram', 'Votre site web'].map((channel, i) => (
                  <span key={i} className="text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(28,24,16,0.08)', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(28,24,16,0.6)' }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isDark ? '#4ade80' : '#16a34a' }} />
                    {channel}
                  </span>
                ))}
                <span className="text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(28,24,16,0.08)', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(28,24,16,0.3)' }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(28,24,16,0.2)' }} />
                  TikTok
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(200,56,29,0.15)', color: t.accent }}>bientôt</span>
                </span>
              </div>

              {/* Stats */}
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md landing-fade-in landing-delay-3">
                <div>
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>5h+</div>
                  <div className="text-xs mt-1" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(28,24,16,0.4)' }}>gagnées / semaine</div>
                </div>
                <div style={{ borderLeft: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(28,24,16,0.1)', borderRight: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(28,24,16,0.1)', paddingLeft: '1rem' }}>
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>4 canaux</div>
                  <div className="text-xs mt-1" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(28,24,16,0.4)' }}>en 1 clic</div>
                </div>
                <div style={{ paddingLeft: '1rem' }}>
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: t.accent }}>B2B</div>
                  <div className="text-xs mt-1" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(28,24,16,0.4)' }}>Marketplace incluse</div>
                </div>
              </div>
            </div>

            {/* Colonne droite — visuel hero (placeholder pour l'image) */}
            <div className="md:w-1/2 relative landing-fade-in landing-delay-2">
              <div className="absolute inset-0 rounded-3xl blur-[60px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative rounded-2xl overflow-hidden" style={{ border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(28,24,16,0.08)', boxShadow: isDark ? '0 40px 80px -20px rgba(0,0,0,0.5)' : '0 40px 80px -20px rgba(0,0,0,0.12)' }}>
                {/* Image placeholder — remplacer par votre visuel */}
                <div className="aspect-[4/3] flex flex-col items-center justify-center p-8" style={{ background: isDark ? 'linear-gradient(145deg, rgba(200,56,29,0.08) 0%, rgba(30,30,40,0.9) 50%, rgba(120,40,200,0.05) 100%)' : 'linear-gradient(145deg, rgba(184,50,22,0.05) 0%, rgba(255,255,255,0.95) 50%, rgba(184,50,22,0.03) 100%)' }}>
                  {/* Mockup dashboard overlay */}
                  <div className="w-full max-w-md">
                    <div className="rounded-xl p-4 mb-3" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)', border: isDark ? '1px solid rgba(255,255,255,0.06)' : `1px solid ${t.border}` }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(200,56,29,0.15)' }}>
                          <Car className="w-5 h-5" style={{ color: t.accent }} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>BMW Série 3 — 2024</div>
                          <div className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : t.textMuted }}>Publié sur 3 canaux</div>
                        </div>
                        <div className="ml-auto flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isDark ? '#4ade80' : '#16a34a' }} />
                          <span className="text-xs" style={{ color: isDark ? '#4ade80' : '#16a34a' }}>En ligne</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[{ icon: <Image className="w-3.5 h-3.5" />, label: 'Instagram', count: '1.2k vues' }, { icon: <Share2 className="w-3.5 h-3.5" />, label: 'Facebook', count: '890 vues' }, { icon: <Globe className="w-3.5 h-3.5" />, label: 'Vitrine', count: '345 visites' }].map((ch, i) => (
                          <div key={i} className="rounded-lg p-2 text-center" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.5)', border: isDark ? '1px solid rgba(255,255,255,0.05)' : `1px solid ${t.border}` }}>
                            <div className="flex justify-center mb-1" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : t.textMuted }}>{ch.icon}</div>
                            <div className="text-[10px] font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : t.textMuted }}>{ch.label}</div>
                            <div className="text-xs font-bold" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>{ch.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-xl p-3 flex items-center gap-3" style={{ backgroundColor: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.08)', border: isDark ? '1px solid rgba(34,197,94,0.15)' : '1px solid rgba(22,163,74,0.15)' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(22,163,74,0.12)' }}>
                        <HeartHandshake className="w-4 h-4" style={{ color: isDark ? '#4ade80' : '#16a34a' }} />
                      </div>
                      <div>
                        <div className="text-xs font-semibold" style={{ color: isDark ? '#ffffff' : '#1c1810' }}>Nouvelle offre reçue</div>
                        <div className="text-[11px]" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : t.textMuted }}>Auto Center Liège — 28.500€ pour la BMW Série 3</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dégradé vers la section suivante */}
        <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{ background: `linear-gradient(to bottom, transparent, ${t.bg})` }} />
      </section>

      {/* ============================================ */}
      {/* BRIDGE — Intro features                      */}
      {/* ============================================ */}
      <section ref={featuresRef} className="pt-20 pb-12 md:pt-28 md:pb-16 px-6 text-center" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: t.accent }}>COMMENT ÇA MARCHE</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            Un seul outil pour tout piloter
          </h2>
          <p className="text-lg md:text-xl leading-relaxed" style={{ color: t.textSecondary }}>
            Diffusion automatique, présence en ligne, vitrine web, réseau B2B et gestion de stock — tout ce qu'il vous faut pour vendre plus, en un seul endroit.
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURE 1 — Diffusion automatique            */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>DIFFUSION MULTI-CANAL</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Publiez partout en un clic
              </h2>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Un véhicule ajouté = publié partout — Facebook, Instagram, vitrine web et Google" />
                <BulletPoint t={t} text="Descriptions et visuels générés par IA, adaptés à votre ton" />
                <BulletPoint t={t} text="Publication planifiée — Carmin publie au bon moment, même sans vous" />
              </div>
              <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
            </div>
            <div className="md:w-1/2 relative">
              <div className="absolute inset-0 rounded-3xl blur-[80px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <MockupInstagramPost t={t} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURE 2 — Présence en ligne & réseaux      */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6" style={{ backgroundColor: isDark ? 'rgba(24,24,27,0.5)' : t.surface, borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16 flex-row-reverse">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>PRÉSENCE EN LIGNE</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Vos réseaux sociaux, c'est votre carte de visite
              </h2>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Profil actif = professionnel crédible — vos clients vérifient vos réseaux avant de vous appeler" />
                <BulletPoint t={t} text="Stories et visuels brandés à vos couleurs, générés automatiquement" />
                <BulletPoint t={t} text="Page inactive ? Le client passe au concurrent. Carmin garde vos réseaux vivants" />
              </div>
              <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
            </div>
            <div className="md:w-1/2 relative">
              <div className="absolute inset-0 rounded-3xl blur-[80px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <MockupSocialFeed t={t} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURE 3 — Vitrine en ligne                 */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>VITRINE EN LIGNE</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Votre stock, visible là où vos clients cherchent
              </h2>
              <p className="text-base mb-6 leading-relaxed" style={{ color: t.textSecondary }}>
                Les clients demandent à Google et aux IA de trouver leur prochaine voiture. Un stock à jour sur votre site, c'est être là au bon moment.
              </p>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Intégration directe sur votre site ou création d'un site clé en main par Carmin" />
                <BulletPoint t={t} text="Votre vitrine, votre stock — zéro concurrent, zéro distraction" />
                <BulletPoint t={t} text="Optimisé pour Google et les IA — ChatGPT, Perplexity vous trouvent et vous recommandent" />
              </div>
              <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
            </div>
            <div className="md:w-1/2 relative">
              <div className="absolute inset-0 rounded-3xl blur-[80px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <MockupVitrine t={t} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURE 4 — Réseau B2B                       */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6" style={{ backgroundColor: isDark ? 'rgba(24,24,27,0.5)' : t.surface, borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16 flex-row-reverse">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>RÉSEAU PROFESSIONNEL B2B</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Achetez, vendez et négociez entre pros
              </h2>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Réseau exclusif de marchands vérifiés — enrichissez votre flotte ou écoulez vos véhicules" />
                <BulletPoint t={t} text="Négociation intégrée : offres, contre-offres, tout en ligne — sans intermédiaire" />
                <BulletPoint t={t} text="Prise de rendez-vous simplifiée : créneaux, confirmations et rappels automatiques" />
              </div>
              <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
            </div>
            <div className="md:w-1/2 relative">
              <div className="absolute inset-0 rounded-3xl blur-[80px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <MockupMarketplace t={t} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FEATURE 5 — Gestion de stock                 */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>GESTION DE STOCK</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Tout votre stock dans un seul tableau de bord
              </h2>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Importez depuis vos portails existants ou ajoutez manuellement en quelques clics" />
                <BulletPoint t={t} text="Statut en temps réel : en stock, publié, en négociation, vendu" />
                <BulletPoint t={t} text="Statistiques de performance et insights IA pour optimiser vos ventes" />
              </div>
              <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accent, color: t.accentText }}>Essayer gratuitement</button>
            </div>
            <div className="md:w-1/2 relative">
              <div className="absolute inset-0 rounded-3xl blur-[80px] opacity-20" style={{ backgroundColor: t.accent }} />
              <div className="relative">
                <MockupDashboard t={t} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING                                      */}
      {/* ============================================ */}
      <section ref={pricingRef} className="py-20 md:py-28 px-6" style={{ backgroundColor: isDark ? 'rgba(24,24,27,0.5)' : t.surface, borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choisissez la formule qui vous convient</h2>
            <p className="text-lg" style={{ color: t.textSecondary }}>Marketplace B2B incluse dans chaque formule — sans frais cachés ni commission.</p>
            <div className="inline-block mt-4">
              <span className="text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap" style={{ background: `linear-gradient(to right, ${t.accent}, ${t.accentLight})`, color: '#ffffff' }}>OFFRE DE LANCEMENT — FÉVRIER 2026 · -50% À VIE</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* PLAN: Réseaux Sociaux */}
            <div
              className="rounded-2xl p-6 md:p-8 relative cursor-pointer transition-all"
              style={{
                backgroundColor: t.card,
                border: selectedPlan === 'social' ? `2px solid ${t.accent}` : `1px solid ${t.border}`,
                boxShadow: selectedPlan === 'social' ? `0 25px 50px -12px ${t.shadow}` : 'none',
              }}
              onClick={() => setSelectedPlan('social')}
            >
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="w-5 h-5" style={{ color: t.accent }} />
                <h3 className="text-lg font-bold">Réseaux Sociaux</h3>
              </div>
              <p className="text-sm mb-5" style={{ color: t.textSecondary }}>Diffusez vos annonces sur les réseaux</p>

              <div className="mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg line-through" style={{ color: t.textMuted }}>149€</span>
                  <span className="text-3xl font-bold">74,50€</span>
                </div>
                <p className="text-sm" style={{ color: t.textSecondary }}>HTVA / mois</p>
              </div>

              <div className="space-y-2.5 mb-6">
                <PricingFeature t={t} text="Gestion de stock complète" />
                <PricingFeature t={t} text="Diffusion réseaux sociaux automatique" />
                <PricingFeature t={t} text="Descriptions & visuels IA" />
                <PricingFeature t={t} text="Marketplace B2B entre pros" />
                <PricingFeature t={t} text="Import depuis vos portails existants" />
                <PricingFeature t={t} text="Onboarding personnalisé (30 min)" />
              </div>

              <button onClick={(e) => { e.stopPropagation(); setSelectedPlan('social'); scrollToSignup(); }} className="w-full font-semibold py-3 rounded-xl transition-all hover:shadow-lg flex items-center justify-center gap-2" style={{ backgroundColor: selectedPlan === 'social' ? t.accent : 'transparent', color: selectedPlan === 'social' ? t.accentText : t.accent, border: selectedPlan === 'social' ? 'none' : `1px solid ${t.accent}` }}>
                Choisir <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* PLAN: Tout Inclus (POPULAIRE) */}
            <div
              className="rounded-2xl p-6 md:p-8 relative cursor-pointer transition-all"
              style={{
                backgroundColor: t.card,
                border: selectedPlan === 'all' ? `2px solid ${t.accent}` : `1px solid ${t.border}`,
                boxShadow: `0 25px 50px -12px ${t.shadow}`,
              }}
              onClick={() => setSelectedPlan('all')}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap" style={{ background: `linear-gradient(to right, ${t.accent}, ${t.accentLight})`, color: '#ffffff' }}>LE PLUS POPULAIRE</span>
              </div>

              <div className="flex items-center gap-2 mb-1 pt-2">
                <Layers className="w-5 h-5" style={{ color: t.accent }} />
                <h3 className="text-lg font-bold">Tout Inclus</h3>
              </div>
              <p className="text-sm mb-5" style={{ color: t.textSecondary }}>Réseaux sociaux + vitrine web + marketplace</p>

              <div className="mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg line-through" style={{ color: t.textMuted }}>249€</span>
                  <span className="text-3xl font-bold">124,50€</span>
                </div>
                <p className="text-sm" style={{ color: t.textSecondary }}>HTVA / mois · <span className="font-semibold" style={{ color: t.accent }}>meilleur rapport</span></p>
              </div>

              <div className="space-y-2.5 mb-6">
                <PricingFeature t={t} text="Gestion de stock complète" />
                <PricingFeature t={t} text="Diffusion réseaux sociaux automatique" />
                <PricingFeature t={t} text="Descriptions & visuels IA" />
                <PricingFeature t={t} text="Vitrine en ligne SEO-optimisée" />
                <PricingFeature t={t} text="Marketplace B2B entre pros" />
                <PricingFeature t={t} text="Négociation & rendez-vous intégrés" />
                <PricingFeature t={t} text="Import depuis vos portails existants" />
                <PricingFeature t={t} text="Onboarding personnalisé (30 min)" />
              </div>

              <button onClick={(e) => { e.stopPropagation(); setSelectedPlan('all'); scrollToSignup(); }} className="w-full font-semibold py-3 rounded-xl transition-all hover:shadow-lg flex items-center justify-center gap-2" style={{ backgroundColor: t.accent, color: t.accentText }}>
                Choisir <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* PLAN: Vitrine Web */}
            <div
              className="rounded-2xl p-6 md:p-8 relative cursor-pointer transition-all"
              style={{
                backgroundColor: t.card,
                border: selectedPlan === 'vitrine' ? `2px solid ${t.accent}` : `1px solid ${t.border}`,
                boxShadow: selectedPlan === 'vitrine' ? `0 25px 50px -12px ${t.shadow}` : 'none',
              }}
              onClick={() => setSelectedPlan('vitrine')}
            >
              <div className="flex items-center gap-2 mb-1">
                <Globe className="w-5 h-5" style={{ color: t.accent }} />
                <h3 className="text-lg font-bold">Vitrine Web</h3>
              </div>
              <p className="text-sm mb-5" style={{ color: t.textSecondary }}>Votre showroom en ligne SEO-optimisé</p>

              <div className="mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-lg line-through" style={{ color: t.textMuted }}>149€</span>
                  <span className="text-3xl font-bold">74,50€</span>
                </div>
                <p className="text-sm" style={{ color: t.textSecondary }}>HTVA / mois</p>
              </div>

              <div className="space-y-2.5 mb-6">
                <PricingFeature t={t} text="Gestion de stock complète" />
                <PricingFeature t={t} text="Vitrine en ligne SEO-optimisée" />
                <PricingFeature t={t} text="Descriptions & visuels IA" />
                <PricingFeature t={t} text="Marketplace B2B entre pros" />
                <PricingFeature t={t} text="Import depuis vos portails existants" />
                <PricingFeature t={t} text="Onboarding personnalisé (30 min)" />
              </div>

              <button onClick={(e) => { e.stopPropagation(); setSelectedPlan('vitrine'); scrollToSignup(); }} className="w-full font-semibold py-3 rounded-xl transition-all hover:shadow-lg flex items-center justify-center gap-2" style={{ backgroundColor: selectedPlan === 'vitrine' ? t.accent : 'transparent', color: selectedPlan === 'vitrine' ? t.accentText : t.accent, border: selectedPlan === 'vitrine' ? 'none' : `1px solid ${t.accent}` }}>
                Choisir <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* OPTION: Site web complet */}
          <div className="rounded-2xl p-6 md:p-8 text-center" style={{ backgroundColor: t.card, border: `1px solid ${t.border}` }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Globe className="w-5 h-5" style={{ color: t.accent }} />
              <h3 className="text-lg font-bold">Besoin d'un site web complet ?</h3>
            </div>
            <p className="text-sm mb-3 max-w-xl mx-auto" style={{ color: t.textSecondary }}>
              Vous n'avez pas encore de site internet ? Notre équipe vous crée un site performant, moderne et optimisé SEO, entièrement sur mesure pour votre garage.
            </p>
            <p className="text-2xl font-bold mb-1">À partir de 999€</p>
            <p className="text-sm mb-4" style={{ color: t.textMuted }}>Paiement unique · ajouté à n'importe quelle formule</p>
            <button onClick={scrollToSignup} className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all hover:shadow-lg" style={{ backgroundColor: t.accentBg, color: t.accent, border: `1px solid ${t.accentBorder}` }}>
              En savoir plus lors de l'onboarding
            </button>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: t.textMuted }}>Sans engagement — annulation à tout moment</p>
        </div>
      </section>

      {/* ============================================ */}
      {/* ONBOARDING                                   */}
      {/* ============================================ */}
      <section className="py-20 md:py-28 px-6" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex items-center gap-16">
            <div className="md:w-1/2 mb-12 md:mb-0">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: t.accent }}>ONBOARDING INCLUS</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                On vous accompagne dès le départ
              </h2>
              <div className="space-y-4 mb-8">
                <BulletPoint t={t} text="Appel de 30 minutes avec notre équipe dès votre inscription" />
                <BulletPoint t={t} text="On configure tout ensemble : stock, réseaux, vitrine, branding" />
                <BulletPoint t={t} text="Premier véhicule publié avant la fin de l'appel — opérationnel immédiatement" />
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="rounded-2xl p-8 space-y-6" style={{ backgroundColor: t.card, border: `1px solid ${t.border}` }}>
                <StepItem t={t} number="1" title="Inscrivez-vous" desc="Créez votre compte et réservez votre créneau d'onboarding" />
                <StepItem t={t} number="2" title="On configure tout ensemble" desc="Stock, réseaux sociaux, vitrine, branding — en 30 minutes" />
                <StepItem t={t} number="3" title="C'est parti" desc="Votre premier véhicule est publié, votre vitrine est en ligne" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* TRUST                                        */}
      {/* ============================================ */}
      <section className="py-16 md:py-20 px-6" style={{ backgroundColor: isDark ? 'rgba(24,24,27,0.5)' : t.surface, borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: t.accentBg }}>
            <Shield className="w-6 h-6" style={{ color: t.accent }} />
          </div>
          <h3 className="text-xl md:text-2xl font-bold mb-2">Réservé aux professionnels de l'automobile</h3>
          <p className="text-base" style={{ color: t.textSecondary }}>
            L'accès à Carmin nécessite une activité de marchand automobile établie. Chaque inscription est vérifiée par notre équipe.
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* SIGNUP FORM                                  */}
      {/* ============================================ */}
      <section ref={signupRef} className="py-20 md:py-28 px-6" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Créez votre compte</h2>
            <p style={{ color: t.textSecondary }}>
              Inscrivez-vous et bénéficiez de l'offre de lancement
              à <strong style={{ color: t.text }}>{selectedPlan === 'all' ? '124,50€' : '74,50€'} HTVA/mois à vie</strong>.
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="rounded-2xl p-6 md:p-8 space-y-4" style={{ backgroundColor: t.card, border: `1px solid ${t.border}` }}>
              {/* Plan selection */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: t.textSecondary }}>Formule choisie</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'social', label: 'Réseaux Sociaux', price: '74,50€', icon: <Share2 className="w-4 h-4" /> },
                    { key: 'all', label: 'Tout Inclus', price: '124,50€', icon: <Layers className="w-4 h-4" /> },
                    { key: 'vitrine', label: 'Vitrine Web', price: '74,50€', icon: <Globe className="w-4 h-4" /> },
                  ].map((plan) => (
                    <button
                      key={plan.key}
                      type="button"
                      onClick={() => setSelectedPlan(plan.key)}
                      className="p-3 rounded-xl text-center transition-all relative"
                      style={{
                        backgroundColor: selectedPlan === plan.key ? t.accentBg : t.input,
                        border: selectedPlan === plan.key ? `2px solid ${t.accent}` : `1px solid ${t.borderSolid}`,
                        color: selectedPlan === plan.key ? t.accent : t.textSecondary,
                      }}
                    >
                      <div className="flex justify-center mb-1" style={{ color: selectedPlan === plan.key ? t.accent : t.textMuted }}>{plan.icon}</div>
                      <div className="text-xs font-semibold" style={{ color: selectedPlan === plan.key ? t.text : t.textSecondary }}>{plan.label}</div>
                      <div className="text-xs mt-0.5 font-bold" style={{ color: t.accent }}>{plan.price}/mois</div>
                      {plan.key === 'all' && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: t.accent, color: '#ffffff' }}>POPULAIRE</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <FormField t={t} label="Votre nom" required type="text" value={name} onChange={setName} placeholder="Jean Dupont" />
                <FormField t={t} label="Entreprise" required type="text" value={company} onChange={setCompany} placeholder="Auto Center Bruxelles" />
              </div>
              <FormField t={t} label="Numéro de TVA" required type="text" value={vatNumber} onChange={setVatNumber} placeholder="BE0XXX.XXX.XXX" />
              <FormField t={t} label="Adresse du siège" required type="text" value={address} onChange={setAddress} placeholder="Rue de la Loi 1, 1000 Bruxelles" />
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField t={t} label="Email professionnel" required type="email" value={email} onChange={setEmail} placeholder="contact@votregarage.be" />
                <FormField t={t} label="Téléphone" required type="tel" value={phone} onChange={setPhone} placeholder="+32 4XX XX XX XX" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField t={t} label="Site web actuel" type="url" value={website} onChange={setWebsite} placeholder="https://votregarage.be" />
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: t.textSecondary }}>Taille du stock</label>
                  <select value={stockSize} onChange={(e) => setStockSize(e.target.value)} className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 transition-all" style={{ backgroundColor: t.input, border: `1px solid ${t.borderSolid}`, color: t.text, '--tw-ring-color': t.accent }}>
                    <option value="">Sélectionner</option>
                    <option value="1-10">1 à 10 véhicules</option>
                    <option value="11-30">11 à 30 véhicules</option>
                    <option value="31-50">31 à 50 véhicules</option>
                    <option value="50+">Plus de 50 véhicules</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: t.textSecondary }}>Portails utilisés actuellement</label>
                <select value={portals} onChange={(e) => setPortals(e.target.value)} className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 transition-all" style={{ backgroundColor: t.input, border: `1px solid ${t.borderSolid}`, color: t.text, '--tw-ring-color': t.accent }}>
                  <option value="">Sélectionner</option>
                  <option value="autoscout24">AutoScout24</option>
                  <option value="2ememain">2ememain</option>
                  <option value="multiple">Plusieurs portails</option>
                  <option value="none">Aucun pour le moment</option>
                </select>
              </div>

              {/* Option site web complet */}
              <div
                className="rounded-xl p-4 cursor-pointer transition-all"
                style={{
                  backgroundColor: wantWebsite ? t.accentBg : t.input,
                  border: wantWebsite ? `2px solid ${t.accent}` : `1px solid ${t.borderSolid}`,
                }}
                onClick={() => setWantWebsite(!wantWebsite)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: wantWebsite ? t.accent : 'transparent', border: wantWebsite ? 'none' : `2px solid ${t.borderSolid}` }}>
                    {wantWebsite && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Je souhaite un site web complet</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: t.accentBg, color: t.accent }}>dès 999€</span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: t.textMuted }}>Site performant, moderne et optimisé SEO — on en discute lors de l'onboarding</p>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="w-full font-semibold py-4 rounded-xl transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 text-lg mt-2" style={{ backgroundColor: t.accent, color: t.accentText }}>
                {submitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>S'inscrire — {selectedPlan === 'all' ? '124,50€' : '74,50€'}/mois<ChevronRight className="w-5 h-5" /></>}
              </button>
              <div className="flex items-center justify-center gap-6 pt-2 text-xs" style={{ color: t.textMuted }}>
                <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: t.greenText }} /> Onboarding inclus</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: t.greenText }} /> Sans engagement</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: t.greenText }} /> Marketplace incluse</span>
              </div>
            </form>
          ) : (
            <div className="text-center py-12 rounded-2xl" style={{ backgroundColor: t.card, border: `1px solid ${t.border}` }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: t.greenBg }}><Check className="w-8 h-8" style={{ color: t.greenText }} /></div>
              <h3 className="text-xl font-bold mb-2">Inscription reçue !</h3>
              <p className="max-w-sm mx-auto mb-4" style={{ color: t.textSecondary }}>Notre équipe vérifiera votre profil et vous contactera sous 48h pour planifier votre onboarding.</p>
              <p className="text-sm" style={{ color: t.textMuted }}>Vous recevrez un email de confirmation à {email || 'votre adresse'}.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6" style={{ borderTop: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Car className="h-5 w-5" style={{ color: t.accent }} />
            <span className="font-semibold">Carmin</span>
          </div>
          <div className="flex items-center gap-4 text-sm" style={{ color: t.textMuted }}>
            <span>© {new Date().getFullYear()} Carmin — Belgique</span>
            <a href="/privacy" className="hover:underline" style={{ color: t.textMuted }}>Politique de confidentialité</a>
          </div>
        </div>
      </footer>
    </div>
  );
};


/* ================================================= */
/* MOCKUP PLACEHOLDERS                               */
/* ================================================= */

const MockupInstagramPost = ({ t }) => (
  <div className="relative">
    <div className="absolute -top-3 -right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
      SCREENSHOT: Post Instagram généré
    </div>
    <div className="rounded-2xl overflow-hidden max-w-sm mx-auto" style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: `0 25px 50px -12px ${t.shadow}` }}>
      {/* Instagram header */}
      <div className="flex items-center gap-3 p-3" style={{ borderBottom: `1px solid ${t.border}` }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: t.accentBg }}>
          <Car className="w-4 h-4" style={{ color: t.accent }} />
        </div>
        <div>
          <p className="text-xs font-semibold">autocenter_bruxelles</p>
          <p className="text-[10px]" style={{ color: t.textMuted }}>Sponsorisé</p>
        </div>
      </div>
      {/* Car image placeholder */}
      <div className="aspect-square flex flex-col items-center justify-center relative" style={{ background: `linear-gradient(to bottom right, ${t.hover}, ${t.surface})` }}>
        <Car className="w-16 h-16 mb-3" style={{ color: t.textDim }} />
        <p className="text-xs" style={{ color: t.textMuted }}>Photo du véhicule</p>
        <div className="absolute bottom-3 right-3 text-sm font-bold px-3 py-1.5 rounded-lg shadow-lg" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
          24.990 €
        </div>
        <div className="absolute top-3 left-3 backdrop-blur-sm text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5" style={{ backgroundColor: isDarkTheme(t) ? 'rgba(17,17,20,0.80)' : 'rgba(255,255,255,0.85)', color: t.text }}>
          <Car className="w-3 h-3" style={{ color: t.accent }} />
          Auto Center
        </div>
      </div>
      {/* Engagement bar */}
      <div className="p-3">
        <div className="flex items-center gap-4 mb-2" style={{ color: t.textSecondary }}>
          <Heart className="w-5 h-5" />
          <MessageCircle className="w-5 h-5" />
          <Send className="w-5 h-5" />
          <Bookmark className="w-5 h-5 ml-auto" />
        </div>
        <p className="text-xs"><span className="font-semibold">autocenter_bruxelles</span> <span style={{ color: t.textSecondary }}>BMW Série 3 320d — 2021 — 45.000 km — Toutes options, état irréprochable...</span></p>
      </div>
    </div>
  </div>
);

const MockupSocialFeed = ({ t }) => (
  <div className="relative">
    <div className="absolute -top-3 -right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
      SCREENSHOT: Feed & Stories
    </div>
    <div className="flex gap-4 items-start justify-center">
      {/* Phone mockup with stories */}
      <div className="rounded-3xl p-3 w-48" style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: `0 25px 50px -12px ${t.shadow}` }}>
        <div className="aspect-[9/16] rounded-2xl flex flex-col items-center justify-between p-4 relative overflow-hidden" style={{ background: `linear-gradient(to bottom, ${t.accentBg}, ${t.surface})` }}>
          <div className="text-center z-10">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: t.accentBg, border: `2px solid ${t.accent}` }}>
              <Car className="w-5 h-5" style={{ color: t.accent }} />
            </div>
            <p className="text-[9px] font-semibold">Auto Center</p>
          </div>
          <div className="z-10 w-full">
            <div className="backdrop-blur rounded-xl p-2.5 text-center" style={{ backgroundColor: isDarkTheme(t) ? 'rgba(17,17,20,0.70)' : 'rgba(255,255,255,0.80)' }}>
              <p className="text-[10px] font-bold">NOUVELLE ARRIVÉE</p>
              <p className="text-[9px] font-bold mt-0.5" style={{ color: t.accent }}>Mercedes Classe A</p>
              <p className="text-[9px]" style={{ color: t.textSecondary }}>À partir de 19.990€</p>
            </div>
          </div>
          <Car className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20" style={{ color: isDarkTheme(t) ? 'rgba(75,85,99,0.30)' : 'rgba(0,0,0,0.06)' }} />
        </div>
      </div>
      {/* Feed cards */}
      <div className="space-y-3 w-44 pt-8">
        {['Audi A4 Avant', 'VW Golf 8', 'Peugeot 3008'].map((car, i) => (
          <div key={i} className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: t.card, border: `1px solid ${t.border}` }}>
            <div className="aspect-video rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: t.hover }}>
              <Car className="w-6 h-6" style={{ color: t.textDim }} />
            </div>
            <p className="text-[10px] font-semibold">{car}</p>
            <p className="text-[9px]" style={{ color: t.textMuted }}>{['18.490€', '22.990€', '26.500€'][i]}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MockupVitrine = ({ t }) => (
  <div className="relative">
    <div className="absolute -top-3 -right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
      SCREENSHOT: Vitrine web
    </div>
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: `0 25px 50px -12px ${t.shadow}` }}>
      {/* Browser bar */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: `1px solid ${t.border}`, backgroundColor: t.surface }}>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.redDot }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.yellowDot }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.greenDotBrowser }} />
        </div>
        <div className="flex-1 rounded-md px-3 py-1 text-[10px] ml-2 flex items-center gap-1.5" style={{ backgroundColor: t.input, color: t.textMuted }}>
          <Globe className="w-3 h-3" />
          www.autocenter-bruxelles.be/stock
        </div>
      </div>
      {/* Website content */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: t.accentBg }}>
              <Car className="w-3 h-3" style={{ color: t.accent }} />
            </div>
            <span className="text-xs font-bold">Auto Center Bruxelles</span>
          </div>
          <div className="flex gap-2 text-[9px]" style={{ color: t.textMuted }}>
            <span>Accueil</span>
            <span className="font-semibold" style={{ color: t.text }}>Stock</span>
            <span>Contact</span>
          </div>
        </div>
        <div className="rounded-lg px-3 py-2 mb-4 flex items-center gap-2" style={{ backgroundColor: t.input, border: `1px solid ${t.border}` }}>
          <Search className="w-3 h-3" style={{ color: t.textMuted }} />
          <span className="text-[10px]" style={{ color: t.textMuted }}>Rechercher par marque, modèle...</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { name: 'BMW 320d', price: '24.990€', km: '45.000 km' },
            { name: 'Audi A4', price: '28.500€', km: '32.000 km' },
            { name: 'Mercedes A180', price: '19.990€', km: '58.000 km' },
            { name: 'VW Golf 8', price: '22.990€', km: '28.000 km' },
            { name: 'Peugeot 3008', price: '26.500€', km: '41.000 km' },
            { name: 'Volvo XC40', price: '31.900€', km: '22.000 km' },
          ].map((car, i) => (
            <div key={i} className="rounded-lg p-2" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}>
              <div className="aspect-video rounded flex items-center justify-center mb-1.5" style={{ backgroundColor: t.hover }}>
                <Car className="w-4 h-4" style={{ color: t.textDim }} />
              </div>
              <p className="text-[9px] font-semibold truncate">{car.name}</p>
              <p className="text-[8px]" style={{ color: t.textMuted }}>{car.km}</p>
              <p className="text-[9px] font-bold" style={{ color: t.accent }}>{car.price}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MockupMarketplace = ({ t }) => (
  <div className="relative">
    <div className="absolute -top-3 -right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
      SCREENSHOT: Marketplace B2B
    </div>
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: `0 25px 50px -12px ${t.shadow}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-2">
          <HeartHandshake className="w-4 h-4" style={{ color: t.accent }} />
          <span className="text-xs font-bold">Marketplace B2B</span>
        </div>
        <div className="flex gap-2 text-[9px]">
          <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: t.accentBg, color: t.accent }}>3 nouvelles offres</span>
        </div>
      </div>
      <div className="p-3 space-y-2.5">
        {[
          { name: 'Audi Q5 S-Line 2022', price: '34.500€', dealer: 'Motors Liège', badge: 'Nouvelle offre', badgeColor: t.greenText, badgeBg: t.greenBg },
          { name: 'Mercedes GLC 300d', price: '41.900€', dealer: 'Auto Premium Gent', badge: 'Contre-offre', badgeColor: t.orangeText, badgeBg: t.orangeBg },
          { name: 'BMW X3 xDrive20d', price: '29.900€', dealer: 'Garage Central Namur', badge: 'RDV confirmé', badgeColor: t.blueText, badgeBg: t.blueBg },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}>
            <div className="w-16 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.hover }}>
              <Car className="w-5 h-5" style={{ color: t.textDim }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{item.name}</p>
              <p className="text-[10px]" style={{ color: t.textMuted }}>{item.dealer}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs font-bold" style={{ color: t.accent }}>{item.price}</p>
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{ color: item.badgeColor, backgroundColor: item.badgeBg }}>{item.badge}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 pb-3">
        <div className="rounded-xl p-3" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}>
          <p className="text-[10px] mb-2" style={{ color: t.textMuted }}>Négociation en cours — Audi Q5 S-Line</p>
          <div className="space-y-1.5">
            <div className="flex justify-end"><span className="text-[10px] px-2.5 py-1 rounded-lg" style={{ backgroundColor: t.accentBg, color: t.accent }}>Votre offre : 32.000€</span></div>
            <div className="flex justify-start"><span className="text-[10px] px-2.5 py-1 rounded-lg" style={{ backgroundColor: t.hover, color: t.textSecondary }}>Contre-offre : 33.500€</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MockupDashboard = ({ t }) => (
  <div className="relative">
    <div className="absolute -top-3 -right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: t.accent, color: '#ffffff' }}>
      SCREENSHOT: Tableau de bord
    </div>
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: `0 25px 50px -12px ${t.shadow}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-2">
          <Car className="w-4 h-4" style={{ color: t.accent }} />
          <span className="text-xs font-bold">Carmin</span>
        </div>
        <div className="flex gap-3 text-[9px]" style={{ color: t.textMuted }}>
          <span style={{ color: t.text }}>Dashboard</span>
          <span>Stock</span>
          <span>Publications</span>
          <span>Marketplace</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 p-3">
        {[
          { label: 'En stock', value: '24', color: t.text },
          { label: 'Publiés', value: '18', color: t.greenText },
          { label: 'En négo', value: '5', color: t.orangeText },
          { label: 'Vendus ce mois', value: '7', color: t.accent },
        ].map((stat, i) => (
          <div key={i} className="rounded-lg p-2.5 text-center" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}>
            <p className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</p>
            <p className="text-[9px]" style={{ color: t.textMuted }}>{stat.label}</p>
          </div>
        ))}
      </div>
      <div className="px-3 pb-3">
        <p className="text-[10px] mb-2 font-medium" style={{ color: t.textMuted }}>Derniers véhicules</p>
        <div className="space-y-1.5">
          {[
            { name: 'BMW 320d Touring', status: 'Publié', statusColor: t.greenText, statusBg: t.greenBg, date: 'Il y a 2h' },
            { name: 'Audi A4 Avant 2.0 TDI', status: 'En stock', statusColor: t.textSecondary, statusBg: t.hover, date: 'Hier' },
            { name: 'VW Golf 8 R-Line', status: 'En négo', statusColor: t.orangeText, statusBg: t.orangeBg, date: 'Il y a 3j' },
          ].map((v, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}>
              <div className="w-10 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.hover }}>
                <Car className="w-3.5 h-3.5" style={{ color: t.textDim }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold truncate">{v.name}</p>
                <p className="text-[8px]" style={{ color: t.textMuted }}>{v.date}</p>
              </div>
              <span className="text-[9px] font-medium px-2 py-0.5 rounded-full" style={{ color: v.statusColor, backgroundColor: v.statusBg }}>{v.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);


/* ================================================= */
/* SHARED SUB-COMPONENTS                             */
/* ================================================= */

const isDarkTheme = (t) => t.bg === '#111114';

const BulletPoint = ({ t, text }) => (
  <div className="flex items-start gap-3">
    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: t.accent }} />
    <p className="text-sm leading-relaxed" style={{ color: t.textSecondary }}>{text}</p>
  </div>
);

const PricingFeature = ({ t, text }) => (
  <div className="flex items-center gap-2">
    <Check className="w-4 h-4 flex-shrink-0" style={{ color: t.accent }} />
    <span className="text-sm" style={{ color: t.textSecondary }}>{text}</span>
  </div>
);

const StepItem = ({ t, number, title, desc }) => (
  <div className="flex items-start gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: t.accentBg, border: `1px solid ${t.accentBorder}` }}>
      <span className="text-sm font-bold" style={{ color: t.accent }}>{number}</span>
    </div>
    <div>
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-xs mt-0.5" style={{ color: t.textSecondary }}>{desc}</p>
    </div>
  </div>
);

const TrustItem = ({ t, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: t.accentBg }}>
      <Check className="w-5 h-5" style={{ color: t.accent }} />
    </div>
    <div>
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm" style={{ color: t.textSecondary }}>{description}</p>
    </div>
  </div>
);

const FormField = ({ t, label, required, type, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5" style={{ color: t.textSecondary }}>{label} {required && '*'}</label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 transition-all" style={{ backgroundColor: t.input, border: `1px solid ${t.borderSolid}`, color: t.text, '--tw-ring-color': t.accent }} />
  </div>
);

export default LandingPage;
